import styled, { css } from 'styled-components/macro';
import Row from 'components/data-visualization/Table/Row';
import CountryFlag from 'components/base/CountryFlag';
import { Link as RouterLink } from 'react-router-dom';

import { definitionValue } from 'styles/base-classes';
import modularScale from 'styles/helpers/modular-scale';
import Cell from 'components/data-visualization/Table/Cell';
import { ReactComponent as Caret } from 'icons/icon-toggle-caret.svg';
import ColumnHeader from 'components/data-visualization/Table/Header';
import { Bullet } from 'components/utility/PositionArrow/PositionArrow';
import {
  HeaderButton,
  HeaderContent,
  Header as HeaderDropDown,
  StyledTableHead,
} from './../../../data-visualization/Table/DropdownHead.style';

interface IObject {
  [key: string]: any;
}

export const MobileColumn = styled('div')`
  text-transform: uppercase;
  text-align: center;
  margin-top: 5px;
  font-size: 11px;
  color: #8da5bf;
  width: 49px;
`;

export const TableRow = styled(Row)<IObject>`
  display: flex;
  flex-flow: row wrap;
  padding-bottom: ${({ theme }) => theme.spacing.xsmall};
  border-bottom: 1px solid ${({ theme }) => theme.Grey};
  background: ${({ theme, isGlobalRow }) => isGlobalRow && theme.Sand};

  ${({ isSmallScreen, isExpanded }) =>
    isSmallScreen &&
    `

    padding-bottom: 0;
    padding-left: 0.5em;
    & > td {
      padding-top: 0;
      padding-bottom: 2px;
      & > div {
        padding-bottom: 0;
      }
    }
    ${isExpanded && 'border-bottom: none;'}
  `}

  &:hover {
    background: ${({ theme, isGlobalRow }) => isGlobalRow && theme.LightGrey};
  }

  @media screen and (min-width: 1024px) {
    display: table-row;
    padding-bottom: 0;
    border-bottom: 0;

    td {
      border-bottom: 1px solid ${({ theme }) => theme.Grey};
    }
  }
  ${({ isTopLists }) =>
    isTopLists &&
    `
    @media screen and (min-width: 960px) {
      display: table-row;

      td {
        border-bottom: 1px solid #D4D8DD;
      }

      ${TableCell} {
        display: table-cell;
      }
    }
  `}
`;

export const PaginationTableRow = styled(TableRow)`
  width: 100%;
  padding-bottom: 0;

  &:hover {
    background: none;
  }

  /* Disable for a false positive */
  /* stylelint-disable-next-line no-descending-specificity */
  td {
    width: 100%;
  }
`;

export const CellContent = styled('div')<IObject>`
  ${definitionValue};
  font-family: ${({ theme, fontFamily }) =>
    fontFamily === 'openSans'
      ? theme.numbersTypography
      : theme.primaryTypography};
  font-size: 16px;
  font-weight: normal;
  display: ${({ display }) => (display ? `${display} !important` : 'flex')};
  text-transform: ${({ isCapitalize }): string =>
    isCapitalize ? 'capitalize' : 'initial'};
  align-items: center;
  color: #000a29;
  padding: 1px 6px !important;

  @media screen and (max-width: 1064px) {
    padding: 0 !important;
  }

  a {
    color: #000a29;
  }

  ${({ minWidth }) =>
    minWidth &&
    `
    min-width: ${minWidth};
  `}

  ${({ flexColumn }) =>
    flexColumn &&
    `
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  `}

  ${({ withRightPadding }) =>
    withRightPadding &&
    css`
      padding-right: 25px !important;

      @media screen and (max-width: 1400px) {
        padding-right: 5px !important;
      }
    `}
  ${({ textAlign, noWrap, theme }) => css`
    white-space: ${noWrap ? 'nowrap' : 'initial'};
    transition: ${`${theme.transitionDuration} ${theme.timingFunction} transform`};

    @media screen and (min-width: 1024px) {
      text-align: ${textAlign ? textAlign : 'left'};
    }
  `}
`;

export const TableCell = styled(Cell)<any>`
  ${({ isSmallScreen }) => !isSmallScreen && 'flex: 0 0 50%;'}
  display: flex;
  align-items: center;
  padding: 0;
  order: 4;

  ${({ isSmallScreen }) => isSmallScreen && 'padding-top: 0 !important;'}

  > div {
    padding: ${({ theme, isSmallScreen }) =>
      isSmallScreen ? '0' : theme.spacing.xsmall};
    ${({ isSmallScreen }) =>
      isSmallScreen &&
      `
        display: flex;
        align-items: baseline;
      `}
  }

  @media screen and (min-width: 1024px) {
    display: table-cell;
    padding: ${({ theme }) =>
      `${theme.spacing.small} ${theme.spacing.xsmall} ${theme.spacing.small}`};

    &:first-of-type {
      padding-left: ${({ theme }) => theme.spacing.xsmall};
    }

    > div {
      padding: 0;
      display: block;
    }
  }
`;

export const TableCellDiv = styled('div')<any>`
  padding: ${({ theme }) => theme.spacing.xsmall};

  &:first-of-type {
    padding-left: 0;
  }

  &:last-of-type {
    padding-right: 0;
  }

  ${({ isSmallScreen }) => !isSmallScreen && 'flex: 0 0 50%;'}
  display: flex;
  align-items: center;
  padding: 0;
  order: 4;

  ${({ isSmallScreen }) => isSmallScreen && 'padding-top: 0 !important;'}

  > div {
    padding: ${({ theme, isSmallScreen }) =>
      isSmallScreen ? '0' : theme.spacing.xsmall};
    ${({ isSmallScreen }) =>
      isSmallScreen &&
      `
        display: flex;
        align-items: baseline;
      `}
  }

  @media screen and (min-width: 1024px) {
    display: table-cell;
    padding: ${({ theme }) =>
      `${theme.spacing.small} ${theme.spacing.xsmall} ${theme.spacing.small}`};

    &:first-of-type {
      padding-left: ${({ theme }) => theme.spacing.xsmall};
    }

    > div {
      padding: 0;
      display: block;
    }
  }
`;

export const MaxWidthTableCell = styled(TableCell)`
  @media screen and (min-width: 1024px) {
    max-width: 8em;
  }
`;

const FixedWidthTableCell = styled(TableCell)`
  text-align: center;

  @media screen and (min-width: 1024px) {
    width: 5em;
  }
`;

export const CaretTableCell = styled(TableCell)`
  text-align: center;

  @media screen and (min-width: 1024px) {
    width: 3em;
  }
`;

export const MobileFixedWidthTableCell = styled(FixedWidthTableCell)`
  flex: 0 0 auto;
  padding-top: 0.5em;
  padding-bottom: 0.5em;

  @media screen and (min-width: 35em) {
    flex: 0 0 auto;
  }

  &:first-of-type {
    order: 1;
    justify-content: flex-start;
  }

  &:nth-last-child(2) {
    width: 20px;
  }

  &:last-of-type {
    order: 3;
    justify-content: flex-end;
    width: auto;
  }
`;

export const ProductTableCell = styled(TableCell)`
  flex: 1 0 100%;

  @media screen and (min-width: 35em) {
    flex: 0 0 50%;
  }
`;

export const TitleTableCell = styled(TableCell)<any>`
  ${({ isSmallScreen }) => !isSmallScreen && 'flex: 1 0 50%;'}
  order: 2;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
  font-weight: 600;

  @media screen and (min-width: 35em) {
    flex: 1 0 75%;
  }
`;

export const Position = styled('span')`
  font-weight: 600;
  font-size: 16px;
`;

export const TerritoryTableRow = styled(TableRow)<any>`
  padding: ${({ isSmallScreen }) => (isSmallScreen ? '0.1em 0.5em' : '1rem')};
  ${(withRowBorder) =>
    !withRowBorder &&
    css`
      &:last-child > td {
        border-bottom: 0;
      }
    `}
  ${({ isExpanded }) => isExpanded && 'border-bottom: none;'}
`;

export const ColSpanCell = styled('td')`
  /** This is needed for IE11 */
  display: block;
  width: 100%;

  @media screen and (min-width: 1024px) {
    display: table-cell;
    width: auto;
  }
`;

export const ExpandableTableRow = styled(TableRow)`
  background: ${({ theme }) => theme.Sand};
  padding-bottom: 0;
  border-bottom: 0;

  ${({ isExpanded }) =>
    !isExpanded &&
    css`
      display: none !important;
    `};
`;

export const StyledCaret = styled(Caret)<IObject>`
  transition: ${({ theme }) =>
    `transform ${theme.transitionDuration} ${theme.timingFunction}`};
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const StyledCountryFlag = styled(CountryFlag)<IObject>`
  display: inline;

  ${({ countryId }) =>
    countryId === 'WW'
      ? css`
          max-width: 21px;
          vertical-align: text-top;
          margin-right: ${({ theme }) => theme.spacing.xsmall};
        `
      : css`
          vertical-align: initial;
          margin-right: ${({ theme }) => theme.spacing.small};
        `};
`;

export const Header = styled(ColumnHeader)<IObject>`
  color: ${({ theme }) => theme.CasperGrey};
  &,
  &:first-of-type {
    text-align: ${({ textAlign }) => (textAlign ? textAlign : 'left')};
  }
`;

export const NewEntries = styled('span')`
  font-size: ${modularScale(-1)};
  margin-left: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.Blue};
  font-weight: normal;
`;

export const GradientBullet = styled(Bullet)`
  background: ${({ theme }) =>
    `linear-gradient(to bottom, #00b2f6, ${theme.Blue});`};
`;

export const ProductName = styled('span')`
  font-family: ${({ theme }) => theme.primaryTypography};
  font-size: 16px;
`;

export const TerritoryList = styled('div')<any>`
  padding-top: calc(
    ${({ theme }) => theme.spacing.medium} -
      ${({ theme }) => theme.spacing.xsmall}
  );
  padding-left: ${({ theme }) => theme.spacing.xsmall};
  padding-right: ${({ theme }) => theme.spacing.xsmall};
  padding-bottom: ${({ theme }) => theme.spacing.medium};
  display: ${({ isSmallScreen }) => (isSmallScreen ? 'block' : 'flex')};
  flex-flow: row wrap;

  ${({ isSmallScreen }) =>
    isSmallScreen &&
    `
    flex-direction: column;
    border-bottom: 1px solid ${({ theme }: any): string => theme.Grey};
  `}

  @media screen and (max-width: 1024px) {
    border-bottom: 1px solid ${({ theme }: any): string => theme.Grey} !important;
  }

  @media screen and (min-width: 22.5em) {
    padding-top: 0;
    padding-bottom: 0.5em;
  }

  @media screen and (min-width: 1024px) {
    padding-left: calc(5em + ${({ theme }) => theme.spacing.xsmall});
    padding-right: calc(5em + ${({ theme }) => theme.spacing.xsmall});
  }
`;

export const TerritoryItem = styled('li')`
  flex: 1 0 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.xsmall};

  @media screen and (min-width: 22.5em) {
    display: block;
    flex: 0 0 9.5em;
    margin-top: ${({ theme }) => theme.spacing.large};
  }
`;

export const TerritoryItemMobile = styled('div')`
  display: flex;
  justify-content: space-between;
`;

export const Link = styled(RouterLink)`
  color: inherit;
  text-decoration: none;
  border-bottom: 1px solid transparent;

  &:hover {
    border-bottom: 1px solid ${({ theme }) => theme.DeepBlue};
  }
`;

export const CustomerWrapper = styled('div')`
  font-size: 11px !important;
  color: #8da5bf;
  display: flex;
  align-items: center;
  flex-direction: column;
`;

export const CustomerName = styled('p')`
  margin: -3px 0 0 0;
`;

export const CompanyName = styled('p')`
  margin: 0;
`;

export const TerritoryCell = styled('div')<any>`
  text-overflow: ellipsis;
  overflow: hidden;
  width: ${({ isSmallScreen }) => (isSmallScreen ? '14em' : '15em')};
  white-space: nowrap;

  @media screen and (max-width: 1420px) {
    width: 12em;
  }
  @media screen and (max-width: 1290px) {
    width: 9em;
  }
  @media screen and (max-width: 1090px) {
    width: 6em;
  }
`;

export const NumberOfCountries = styled('div')`
  padding-right: 1.5em !important;
`;

export const ContentWrapper = styled('div')`
  padding: 0 5px 0 15px;
`;

export const ChartPositionTableWrapper = styled('div')<{
  isTopCharts?: boolean;
  isTopLists?: boolean;
}>`
  ${HeaderButton} {
    padding: 0;
  }

  ${StyledTableHead} {
    vertical-align: ${({ isTopCharts }) => isTopCharts && 'bottom'};
    ${HeaderDropDown} {
      &:first-of-type {
        ${HeaderContent},
        ${HeaderButton} {
          padding-left: 0.5em;
          text-align: right;
        }
      }

      &:last-of-type {
        ${HeaderContent},
        ${HeaderButton} {
          display: block;
          white-space: normal;
          width: 5em;
          text-align: right;
        }
        div > div {
          margin-left: -1.5em;
        }
      }

      ${({ isTopCharts }) =>
        !isTopCharts &&
        css`
          &:nth-last-child(2) {
            ${HeaderContent},
            ${HeaderButton} {
              display: block;
              white-space: normal;
              width: 6em;
              text-align: right;
              margin-right: -1.8em;
            }
          }
        `}

      ${({ isTopCharts }) =>
        isTopCharts &&
        css`
          ${HeaderContent},
          ${HeaderButton} {
            display: block;
            white-space: normal;
            width: 6em;
            text-align: right;
          }

          &:first-of-type {
            ${HeaderContent},
            ${HeaderButton} {
              text-align: center;
              margin: auto;
              width: auto;
            }
          }

          &:nth-child(2) {
            ${HeaderContent},
            ${HeaderButton} {
              width: auto;
            }
          }
        `}
    }

    ${HeaderDropDown} {
      &:last-of-type {
        ${HeaderContent},
        ${HeaderButton} {
          div {
            text-align: right;
          }
        }
      }
    }
  }

  ${({ isTopCharts }) =>
    isTopCharts &&
    css`
      tbody {
        td,
        span,
        div {
          font-size: 14px;
        }
      }
    `}
  ${({ isTopLists }) =>
    isTopLists &&
    css`
      margin-top: 10px;
    `}
`;
